import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  adamCompaniesQuery,
  adamSettingsQuery,
  adamUsersQuery,
  saveAdamSettings,
  type AdamSettingsValues,
} from '../../api/settingsEaseedeskAdamIntop'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Adam Intop → General settings.
 *
 * Camp cu camp fata de `version-3/easeedesk_settings/adam_intop/index.php` si
 * gest cu gest fata de `public/js/views/easeedesk_settings/adam_intop/index-1.js`.
 *
 * DOUA LUCRURI DIN ECRANUL VECHI CARE NU SE MUTA ASA CUM ERAU
 * ----------------------------------------------------------
 *
 * - FIRMELE BIFATE SE VAD CU NUMELE. Macheta veche desena lista de firme
 *   alese punand ID-UL si ca valoare, si ca eticheta (`<option value="7"> 7
 *   </option>`), si abia cand omul deschidea lista `getCompaniesAI` aducea
 *   numele. Aici numele vin de la inceput, dintr-o singura cerere.
 *
 * - LISTA DE OAMENI SE CERE DIN GRUPUL LUI ADAM. In portal, schimbarea
 *   grupului chema `easeedesk/getGroupUsers` - alt controller - desi grupul
 *   asta are chiar metoda lui, `get_users_by_group`, pe care n-o chema nimeni.
 *   Aici se cheama a lui.
 *
 * Cele trei campuri de stare (`status_new`, `status_remote_control`,
 * `status_customer_confirmation`) sunt citite de controllerul vechi dar NU sunt
 * desenate de macheta - au ramas de la o versiune de dinainte. Nu se deseneaza
 * nici aici; randurile lor din tabela raman neatinse, fiindca salvarea scrie
 * numai campurile pe care le trimite formularul.
 */

/** Cele opt surse de tichete, in ordinea din macheta. */
const SOURCES: Array<[string, string]> = [
  ['can_take_tickets_from_agent', 'Can take tickets from Agent'],
  ['can_take_tickets_from_chat', 'Can take tickets from Chat'],
  ['can_take_tickets_from_portal', 'Can take tickets from Portal'],
  ['can_take_tickets_from_operator', 'Can take tickets from Workorder'],
  ['can_take_tickets_from_collaborate', 'Can take tickets from Collaborate'],
  ['can_take_tickets_from_alarms', 'Can take tickets from Alarms'],
  ['can_take_tickets_from_image_analizer', 'Can take tickets from Attachments Upload'],
  ['can_take_tickets_from_macos_agent', 'Can take tickets from macOS Agent'],
]

export function AdamIntopSettingsPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(adamSettingsQuery())
  const companies = useQuery(adamCompaniesQuery())
  const [values, setValues] = useState<AdamSettingsValues>({})
  const [types, setTypes] = useState<number[]>([])
  const [assigned, setAssigned] = useState<number[]>([])

  useEffect(() => {
    const data = shell.data
    if (!data) return
    setValues(data.values)
    setTypes(data.checked_ticket_types)
    setAssigned(data.assigned_companies)
  }, [shell.data])

  const group = Number(values.group ?? 0)
  /* Oamenii grupului: cei din shell pana la prima schimbare de grup, apoi ai
     grupului ales. */
  const users = useQuery(adamUsersQuery(group))
  const userList = users.data?.users ?? shell.data?.users ?? []

  const save = useMutation({
    mutationFn: () => saveAdamSettings(values, types, assigned),
    onSuccess: () => {
      toast('Settings saved.', 'Success')
      void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'adam_intop', 'settings'] })
    },
    onError: (error) => toast(error instanceof ApiError ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!shell.data) return <Spinner />

  const on = (key: string) => (values[key] ?? '') === 'on' || (values[key] ?? '') === '1'
  const toggle = (key: string) => (checked: boolean) => setValues({ ...values, [key]: checked ? 'on' : 'off' })

  return (
    <div>
      <h4>
        <strong>Adam Intop</strong> General Settings
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage <strong>general settings</strong> for Adam Intop
      </label>
      <hr />
      <form
        method="post"
        id="save-adam-intop-form"
        onSubmit={(e) => {
          e.preventDefault()
          /* Intrebarea din swal, cu acelasi text. */
          if (
            !window.confirm(
              'Are you sure you want to change these settings?\nAll general settings for Adam Intop will be replaced with those in the form.',
            )
          )
            return
          save.mutate()
        }}
      >
        <div className="row">
          <div className="col-md-12">
            <div className="form-group">
              <input
                type="checkbox"
                id="adam-active"
                checked={(values.active ?? '') === '1'}
                onChange={(e) => setValues({ ...values, active: e.target.checked ? '1' : '0' })}
              />{' '}
              <label htmlFor="adam-active" className="control-label">
                <strong>Activate Adam Intop</strong>
              </label>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="adam-group" className="control-label">
                Group (*)
              </label>
              <select
                id="adam-group"
                className="form-control"
                name="group"
                value={values.group ?? ''}
                onChange={(e) => setValues({ ...values, group: e.target.value, user: '' })}
              >
                <option value="">- select -</option>
                {shell.data.departments.map((d) => (
                  <option key={d.ID} value={d.ID}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="adam-user" className="control-label">
                User (*)
              </label>
              <select
                id="adam-user"
                className="form-control"
                name="user"
                value={values.user ?? ''}
                onChange={(e) => setValues({ ...values, user: e.target.value })}
              >
                <option value="">no user selected...</option>
                {userList.map((u) => (
                  <option key={u.ID} value={u.ID}>
                    {u.user_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="adam-agent-version" className="control-label">
                Latest Agent Version (*)
              </label>
              <input
                id="adam-agent-version"
                type="text"
                className="form-control"
                name="latest_agent_version"
                value={values.latest_agent_version ?? ''}
                onChange={(e) => setValues({ ...values, latest_agent_version: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="form-group">
              <input
                type="checkbox"
                id="adam-only-assigned"
                checked={on('run_only_on_assigned_companies')}
                onChange={(e) => toggle('run_only_on_assigned_companies')(e.target.checked)}
              />{' '}
              <label htmlFor="adam-only-assigned" className="control-label">
                Run Adam Intop only on selected companies
              </label>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="assigned_companies" className="control-label">
                Select Companies
              </label>
              <select
                id="assigned_companies"
                className="form-control"
                name="assigned_companies[]"
                multiple
                size={8}
                value={assigned.map(String)}
                onChange={(e) => setAssigned(pick(e.target.selectedOptions))}
              >
                {(companies.data?.companies ?? []).map((c) => (
                  <option key={c.ID} value={c.ID}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="run_only_on_ticket_types" className="control-label">
                Run only on this ticket types
              </label>
              <select
                id="run_only_on_ticket_types"
                className="form-control"
                name="run_only_on_ticket_types[]"
                multiple
                size={8}
                value={types.map(String)}
                onChange={(e) => setTypes(pick(e.target.selectedOptions))}
              >
                {shell.data.ticket_types.map((t) => (
                  <option key={t.ID} value={t.ID}>
                    {t.short_name} ({t.type_name})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="divider">
          <hr />
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="form-group">
              <label className="control-label">Check Ticket Source</label>
            </div>
          </div>
          {SOURCES.map(([key, label]) => (
            <Switch key={key} field={key} label={label} on={on(key)} onChange={toggle(key)} />
          ))}
        </div>

        <div className="divider">
          <hr />
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="form-group">
              <label className="control-label">Log Data</label>
            </div>
          </div>
          <Switch field="log_data" label="Log Adam Intop Data" on={on('log_data')} onChange={toggle('log_data')} />
        </div>

        <div className="divider">
          <hr />
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="form-group">
              <label className="control-label">Recall socket</label>
            </div>
          </div>
          <Switch
            field="recall_socket_v1"
            label="Recall Socket V1"
            on={on('recall_socket_v1')}
            onChange={toggle('recall_socket_v1')}
          />
          <Switch
            field="recall_socket_v2"
            label="Recall Socket V2"
            on={on('recall_socket_v2')}
            onChange={toggle('recall_socket_v2')}
          />
        </div>

        <div className="divider">
          <hr />
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="form-group">
              <label className="control-label">Ticket attachments</label>
            </div>
          </div>
          <Switch
            field="analize_image_after_upload"
            label="Analize image attachment after upload"
            on={on('analize_image_after_upload')}
            onChange={toggle('analize_image_after_upload')}
          />
        </div>

        <div className="form-group mt-2 text-center center-block">
          <button type="submit" id="saveaisettings" className="btn btn-primary" disabled={save.isPending}>
            Save
          </button>
        </div>
      </form>
    </div>
  )
}

function pick(options: HTMLCollectionOf<HTMLOptionElement>): number[] {
  return Array.from(options)
    .map((o) => Number(o.value))
    .filter((n) => n > 0)
}

function Switch({
  field,
  label,
  on,
  onChange,
}: {
  field: string
  label: string
  on: boolean
  onChange(next: boolean): void
}) {
  return (
    <div className="col-md-6">
      <div className="form-group">
        <input
          type="checkbox"
          id={`adam-${field}`}
          className="switchery_edit"
          name={field}
          checked={on}
          onChange={(e) => onChange(e.target.checked)}
        />{' '}
        <label htmlFor={`adam-${field}`} className="control-label">
          {label}
        </label>
      </div>
    </div>
  )
}

export function Forbidden() {
  return (
    <div className="alert alert-danger">
      <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
    </div>
  )
}
